/**
 * ModulesOverlay.js
 *
 * Phase 1 — Ship Upgrades / Modules (Outfitting).
 *
 * Opened from the Shipyard terminal (via the Hangar's "Outfitting" button).
 * Two halves:
 *   1. CORE UPGRADES  — tiered, always-on boosts (cargo / shield / hull / engine).
 *   2. MODULE SLOTS   — special systems you buy/upgrade then EQUIP into the
 *                       active ship's limited module slots.
 *
 * Everything is per-ship and costs credits + raw materials (raw ore units).
 * All effects are recomputed server-side; on every change we bubble up via
 * `onChanged` so the live scene re-applies the combined multipliers.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Wrench, DoorOpen, ChevronLeft, Package, Shield, Heart, Gauge,
  Pickaxe, Crosshair, Radar, Wind, Plus, Check, Coins, Boxes, Gem,
} from 'lucide-react';

const ICONS = {
  package: Package, shield: Shield, heart: Heart, gauge: Gauge,
  pickaxe: Pickaxe, crosshair: Crosshair, radar: Radar, wind: Wind,
};

const ALLOY_NAMES = {
  steel_alloy: 'Steel Alloy', silicon_wafer: 'Silicon Wafer', aluminum_sheet: 'Aluminium Sheet',
  titanium_plate: 'Titanium Plate', copper_circuit: 'Copper Circuit',
  composite_superalloy: 'Composite Superalloy', quantum_composite: 'Quantum Composite',
};

function TierPips({ tier, max }) {
  return (
    <div className="flex items-center gap-1" data-testid="module-tier-pips">
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 w-4 rounded-full ${i < tier ? 'bg-cyan-400' : 'bg-white/10'}`}
        />
      ))}
    </div>
  );
}

function Cost({ cost, alloyCounts }) {
  if (!cost) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-[11px] tabular-nums">
      <span className="inline-flex items-center gap-1 text-amber-200"><Coins className="w-3 h-3" />{cost.credits.toLocaleString()}</span>
      <span className="inline-flex items-center gap-1 text-emerald-200"><Boxes className="w-3 h-3" />{cost.ore}u</span>
      {(cost.alloys || []).map((a) => {
        const have = (alloyCounts || {})[a.id] || 0;
        const ok = have >= a.qty;
        return (
          <span
            key={a.id}
            className={`inline-flex items-center gap-1 px-1 rounded border ${ok ? 'border-fuchsia-400/30 text-fuchsia-200' : 'border-rose-400/40 text-rose-300'}`}
            data-testid={`alloy-req-${a.id}`}
          >
            <Gem className="w-3 h-3" />{a.qty}× {ALLOY_NAMES[a.id] || a.id} <span className="opacity-60">({Math.floor(have)})</span>
          </span>
        );
      })}
    </span>
  );
}

export default function ModulesOverlay({ open, api, onClose, onBackToHangar, onChanged, updateCredits }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/modules/loadout');
      setData(res.data);
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const upgradeCore = useCallback(async (key) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/api/modules/upgrade-core', { key });
      setData(res);
      if (res?.credits != null) updateCredits?.(res.credits);
      onChanged?.();
      toast.success('Core system upgraded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upgrade failed');
    } finally { setBusy(false); }
  }, [api, onChanged, updateCredits]);

  const upgradeModule = useCallback(async (moduleId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/api/modules/upgrade-module', { module_id: moduleId });
      setData(res);
      if (res?.credits != null) updateCredits?.(res.credits);
      onChanged?.();
      toast.success('Module upgraded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upgrade failed');
    } finally { setBusy(false); }
  }, [api, onChanged, updateCredits]);

  const toggleEquip = useCallback(async (moduleId, equip) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/api/modules/equip', { module_id: moduleId, equip });
      setData(res);
      if (res?.credits != null) updateCredits?.(res.credits);
      onChanged?.();
      toast.success(equip ? 'Module installed' : 'Module removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change loadout');
    } finally { setBusy(false); }
  }, [api, onChanged, updateCredits]);

  if (!open) return null;

  const slotsFull = data && data.slots_used >= data.module_slots;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="modules-overlay"
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-cyan-400/25 bg-[#070b12]/95 shadow-[0_0_60px_rgba(60,200,255,0.15)] flex flex-col"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-cyan-300" />
            <div>
              <h2 className="text-lg font-semibold text-cyan-100 tracking-wide">Outfitting · Modules</h2>
              <p className="text-[11px] text-slate-400">
                {data ? `${data.ship_name} · ${data.slots_used}/${data.module_slots} slots used` : 'Upgrade & equip your ship'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-2.5 py-1.5 rounded-lg bg-amber-400/10 border border-amber-300/25 text-amber-200 text-xs tabular-nums inline-flex items-center gap-1" data-testid="modules-credits">
              <Coins className="w-3.5 h-3.5" />{Number(data?.credits || 0).toLocaleString()}
            </div>
            <div className="px-2.5 py-1.5 rounded-lg bg-emerald-400/10 border border-emerald-300/25 text-emerald-200 text-xs tabular-nums inline-flex items-center gap-1" data-testid="modules-materials">
              <Boxes className="w-3.5 h-3.5" />{Number(data?.materials || 0).toLocaleString()}u
            </div>
            {onBackToHangar && (
              <Button variant="ghost" size="sm" onClick={onBackToHangar} data-testid="modules-back-button" className="text-slate-300 hover:text-white">
                <ChevronLeft className="w-4 h-4 mr-1" /> Hangar
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="modules-close-button" className="text-slate-300 hover:text-white">
              <DoorOpen className="w-4 h-4 mr-1" /> Close
            </Button>
          </div>
        </div>

        <div className="overflow-y-auto p-4 space-y-5">
          {loading && <div className="text-center text-slate-400 py-10">Loading outfitting bay…</div>}

          {!loading && data && (
            <>
              {/* Core upgrades */}
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-cyan-300/80 mb-2">Core Upgrades</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.core.map((c) => {
                    const Icon = ICONS[c.icon] || Gauge;
                    return (
                      <div key={c.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`core-card-${c.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-cyan-300" />
                            <span className="text-sm font-semibold text-slate-100">{c.name}</span>
                          </div>
                          <Badge variant="outline" className="text-[10px] border-cyan-400/30 text-cyan-200">Tier {c.tier}/{c.max_tier}</Badge>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1 mb-2">{c.description}</p>
                        <TierPips tier={c.tier} max={c.max_tier} />
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <Cost cost={c.next_cost} alloyCounts={data.alloy_counts} />
                          {c.maxed ? (
                            <Badge className="bg-cyan-500/30 text-cyan-100 text-[10px]"><Check className="w-3 h-3 mr-0.5" /> Maxed</Badge>
                          ) : (
                            <Button
                              size="sm" disabled={busy}
                              onClick={() => upgradeCore(c.id)}
                              data-testid={`core-upgrade-${c.id}`}
                              className="h-7 bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold text-xs"
                            >
                              <Plus className="w-3.5 h-3.5 mr-0.5" /> Upgrade
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Slot modules */}
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-fuchsia-300/80 mb-2">
                  Module Slots <span className="text-slate-500">· {data.slots_used}/{data.module_slots} used</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.modules.map((m) => {
                    const Icon = ICONS[m.icon] || Crosshair;
                    return (
                      <div
                        key={m.id}
                        className={`rounded-xl border p-3 ${m.equipped ? 'border-fuchsia-400/50 bg-fuchsia-500/[0.07]' : 'border-white/10 bg-white/[0.03]'}`}
                        data-testid={`module-card-${m.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-fuchsia-300" />
                            <span className="text-sm font-semibold text-slate-100">{m.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {m.equipped && <Badge className="bg-fuchsia-500/80 text-white text-[10px]">Equipped</Badge>}
                            <Badge variant="outline" className="text-[10px] border-fuchsia-400/30 text-fuchsia-200">Tier {m.tier}/{m.max_tier}</Badge>
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1 mb-2">{m.description}</p>
                        <TierPips tier={m.tier} max={m.max_tier} />
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <Cost cost={m.next_cost} alloyCounts={data.alloy_counts} />
                          <div className="flex items-center gap-1.5">
                            {m.maxed ? (
                              <Badge className="bg-fuchsia-500/30 text-fuchsia-100 text-[10px]"><Check className="w-3 h-3 mr-0.5" /> Maxed</Badge>
                            ) : (
                              <Button
                                size="sm" disabled={busy}
                                onClick={() => upgradeModule(m.id)}
                                data-testid={`module-upgrade-${m.id}`}
                                className="h-7 bg-amber-400/90 hover:bg-amber-300 text-slate-900 font-semibold text-xs"
                              >
                                {m.owned ? <><Plus className="w-3.5 h-3.5 mr-0.5" /> Tier</> : 'Buy'}
                              </Button>
                            )}
                            {m.owned && (
                              m.equipped ? (
                                <Button
                                  size="sm" variant="outline" disabled={busy}
                                  onClick={() => toggleEquip(m.id, false)}
                                  data-testid={`module-unequip-${m.id}`}
                                  className="h-7 text-xs border-white/20 text-slate-300 hover:bg-white/5"
                                >
                                  Unequip
                                </Button>
                              ) : (
                                <Button
                                  size="sm" disabled={busy || slotsFull}
                                  onClick={() => toggleEquip(m.id, true)}
                                  data-testid={`module-equip-${m.id}`}
                                  className={`h-7 text-xs font-semibold ${slotsFull ? 'bg-white/5 text-slate-500' : 'bg-fuchsia-500/90 hover:bg-fuchsia-400 text-white'}`}
                                >
                                  {slotsFull ? 'Slots full' : 'Equip'}
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
